import { useState, useEffect, useCallback } from 'react'
import {
  Trash2,
  RotateCcw,
  FileText,
  Image,
  MoreVertical,
  AlertTriangle,
  Clock,
} from 'lucide-react'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { binService, type BinItem } from '@/lib/api/services/bin'
import { cn } from '@/lib/utils'

type FilterType = 'all' | 'template' | 'asset'

interface DeleteDialogState {
  open: boolean
  type: 'single' | 'multiple' | 'empty'
  item?: BinItem
}

export default function BinPage() {
  const [items, setItems] = useState<BinItem[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterType>('all')
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [counts, setCounts] = useState({ total: 0, templates: 0, assets: 0 })
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    type: 'single',
  })
  const { toast } = useToast()

  const loadItems = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [itemsResponse, countResponse] = await Promise.all([
        binService.getItems({ type: filter === 'all' ? undefined : filter }),
        binService.getCount(),
      ])

      setItems(itemsResponse.items)
      setCounts(countResponse)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bin items')
      toast({
        title: 'Error',
        description: 'Failed to load bin items',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [filter, toast])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleSelectAll = useCallback(() => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(items.map((item) => item.id)))
    }
  }, [items, selectedItems.size])

  const handleSelectItem = useCallback((id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleRestore = useCallback(
    async (item: BinItem) => {
      try {
        setActionLoading(true)
        await binService.restoreItem(item.type, item.id)
        toast({
          title: 'Item Restored',
          description: `"${item.name}" has been restored successfully.`,
        })
        loadItems()
        setSelectedItems(new Set())
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to restore item',
          variant: 'destructive',
        })
      } finally {
        setActionLoading(false)
      }
    },
    [loadItems, toast]
  )

  const handleRestoreSelected = useCallback(async () => {
    if (selectedItems.size === 0) return

    try {
      setActionLoading(true)
      const itemsToRestore = items
        .filter((item) => selectedItems.has(item.id))
        .map((item) => ({ type: item.type, id: item.id }))

      await binService.restoreMultiple(itemsToRestore)
      toast({
        title: 'Items Restored',
        description: `${selectedItems.size} item(s) have been restored successfully.`,
      })
      loadItems()
      setSelectedItems(new Set())
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to restore items',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }, [items, loadItems, selectedItems, toast])

  const handlePermanentDelete = useCallback(
    async (item: BinItem) => {
      try {
        setActionLoading(true)
        await binService.deleteItem(item.type, item.id)
        toast({
          title: 'Item Deleted',
          description: `"${item.name}" has been permanently deleted.`,
        })
        loadItems()
        setSelectedItems(new Set())
        setDeleteDialog({ open: false, type: 'single' })
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to delete item',
          variant: 'destructive',
        })
      } finally {
        setActionLoading(false)
      }
    },
    [loadItems, toast]
  )

  const handleDeleteSelected = useCallback(async () => {
    if (selectedItems.size === 0) return

    try {
      setActionLoading(true)
      const itemsToDelete = items
        .filter((item) => selectedItems.has(item.id))
        .map((item) => ({ type: item.type, id: item.id }))

      await binService.deleteMultiple(itemsToDelete)
      toast({
        title: 'Items Deleted',
        description: `${selectedItems.size} item(s) have been permanently deleted.`,
      })
      loadItems()
      setSelectedItems(new Set())
      setDeleteDialog({ open: false, type: 'multiple' })
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to delete items',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }, [items, loadItems, selectedItems, toast])

  const handleEmptyBin = useCallback(async () => {
    try {
      setActionLoading(true)
      await binService.emptyBin()
      toast({
        title: 'Bin Emptied',
        description: 'All items have been permanently deleted.',
      })
      loadItems()
      setSelectedItems(new Set())
      setDeleteDialog({ open: false, type: 'empty' })
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to empty bin',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }, [loadItems, toast])

  const getItemIcon = (item: BinItem) => {
    if (item.type === 'template') {
      return (
        <div className="p-2 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
          <FileText className="h-4 w-4 text-white" />
        </div>
      )
    }
    return (
      <div className="p-2 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
        <Image className="h-4 w-4 text-white" />
      </div>
    )
  }

  const getTypeLabel = (item: BinItem) => {
    if (item.type === 'template' && item.metadata?.templateType) {
      return `${item.metadata.templateType} Template`
    }
    if (item.type === 'asset' && item.metadata?.assetType) {
      return item.metadata.assetType
    }
    return item.type === 'template' ? 'Template' : 'Asset'
  }

  const filteredCounts = {
    all: counts.total,
    template: counts.templates,
    asset: counts.assets,
  }

  const isAllSelected = items.length > 0 && selectedItems.size === items.length
  const isSomeSelected = selectedItems.size > 0 && selectedItems.size < items.length

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bin"
        description="Deleted items are kept for 30 days before being permanently removed"
        actions={
          counts.total > 0 && (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900"
              onClick={() => setDeleteDialog({ open: true, type: 'empty' })}
              disabled={actionLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Empty Bin
            </Button>
          )
        }
      />

      {/* Warning Banner */}
      <FluentCard className="border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-medium text-amber-900 dark:text-amber-200">
              Items are automatically deleted after 30 days
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Restore items to keep them, or permanently delete them to free up space immediately.
            </p>
          </div>
        </div>
      </FluentCard>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: 'Total Items', value: counts.total, color: 'from-gray-400 to-gray-600', icon: Trash2 },
          { label: 'Templates', value: counts.templates, color: 'from-blue-400 to-blue-600', icon: FileText },
          { label: 'Assets', value: counts.assets, color: 'from-violet-400 to-purple-600', icon: Image },
        ].map((stat) => (
          <FluentCard key={stat.label} className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
              <stat.icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            </div>
          </FluentCard>
        ))}
      </div>

      {/* Filter and Bulk Actions */}
      <FluentCard>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <Select value={filter} onValueChange={(value) => setFilter(value as FilterType)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Items ({filteredCounts.all})</SelectItem>
                <SelectItem value="template">Templates ({filteredCounts.template})</SelectItem>
                <SelectItem value="asset">Assets ({filteredCounts.asset})</SelectItem>
              </SelectContent>
            </Select>

            {selectedItems.size > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedItems.size} selected
              </span>
            )}
          </div>

          {selectedItems.size > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestoreSelected}
                disabled={actionLoading}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30"
                onClick={() => setDeleteDialog({ open: true, type: 'multiple' })}
                disabled={actionLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        {error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button variant="outline" onClick={loadItems}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) {
                          (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate =
                            isSomeSelected
                        }
                      }}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Deleted</TableHead>
                  <TableHead>Deleted By</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-full max-w-[200px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-48">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                          <Trash2 className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                          Bin is empty
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Deleted templates and assets will appear here
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const daysUntilExpiration = binService.getDaysUntilExpiration(item.expiresAt)
                    const isExpiringSoon = daysUntilExpiration <= 7

                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedItems.has(item.id)}
                            onCheckedChange={() => handleSelectItem(item.id)}
                            aria-label={`Select ${item.name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {getItemIcon(item)}
                            <span className="font-medium text-gray-900 dark:text-white">
                              {item.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            {getTypeLabel(item)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">
                            {new Date(item.deletedAt).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">
                            {item.deletedByName || item.deletedBy}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">
                            {binService.formatFileSize(item.size)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 text-sm',
                              isExpiringSoon
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-500 dark:text-gray-400'
                            )}
                          >
                            <Clock className="h-3 w-3" />
                            {daysUntilExpiration} days
                          </span>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                                <MoreVertical className="h-5 w-5 text-gray-400" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 rounded-xl">
                              <DropdownMenuItem
                                onClick={() => handleRestore(item)}
                                disabled={actionLoading}
                                className="rounded-lg gap-2"
                              >
                                <RotateCcw className="h-4 w-4" />
                                Restore
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20 gap-2"
                                onClick={() =>
                                  setDeleteDialog({ open: true, type: 'single', item })
                                }
                                disabled={actionLoading}
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete Permanently
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </FluentCard>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              {deleteDialog.type === 'empty'
                ? 'Empty Bin'
                : deleteDialog.type === 'multiple'
                ? 'Delete Selected Items'
                : 'Delete Item Permanently'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === 'empty' ? (
                <>
                  Are you sure you want to permanently delete all {counts.total} item(s) in the
                  bin? This action cannot be undone.
                </>
              ) : deleteDialog.type === 'multiple' ? (
                <>
                  Are you sure you want to permanently delete {selectedItems.size} selected
                  item(s)? This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to permanently delete "{deleteDialog.item?.name}"? This
                  action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                if (deleteDialog.type === 'empty') {
                  handleEmptyBin()
                } else if (deleteDialog.type === 'multiple') {
                  handleDeleteSelected()
                } else if (deleteDialog.item) {
                  handlePermanentDelete(deleteDialog.item)
                }
              }}
              disabled={actionLoading}
            >
              {actionLoading ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
